<template>
  <div>
    <v-row v-if="priced.length > 0 && $vuetify.breakpoint.mdAndUp" dense class="caption grey--text row-header">
      <v-col md="6" :class="{ 'pl-8': !readonly }">{{ $t('quotations.calc.offered_work') }}</v-col>
      <v-col md="2" class="text-right">{{ $t('quotations.calc.quantity') }}</v-col>
      <v-col md="2" class="text-right">{{ $t('quotations.calc.unit_price') }}</v-col>
      <v-col md="2" class="text-right" :class="{ 'pr-16': !readonly }">{{ $t('quotations.calc.row_total') }}</v-col>
    </v-row>
    <draggable :value="priced" handle=".drag-handle" :disabled="readonly || saving" @input="v => reorder(v, conditional)">
      <div v-for="row in priced" :key="key(row)">
        <quotation-row-editor v-if="row === editingRow" :row="row" :work-methods="workMethods" :items="items" :saving="saving" @done="editingRow = null" @remove="removeRow(row)" />
        <quotation-row-view v-else :row="row" :work-methods="workMethods" :readonly="readonly" :saving="saving" @edit="editingRow = row" @copy="copyRow(row)" @remove="removeRow(row)" />
      </div>
    </draggable>
    <div v-if="priced.length === 0" class="grey--text body-2 py-2">{{ $t('quotations.calc.no_rows') }}</div>

    <template v-if="conditional.length > 0">
      <div class="subtitle-2 mt-4">{{ $t('quotations.calc.conditional_rows') }}</div>
      <draggable :value="conditional" handle=".drag-handle" :disabled="readonly || saving" @input="v => reorder(priced, v)">
        <div v-for="row in conditional" :key="key(row)">
          <quotation-row-editor v-if="row === editingRow" :row="row" :work-methods="workMethods" :items="items" :saving="saving" @done="editingRow = null" @remove="removeRow(row)" />
          <quotation-row-view v-else :row="row" :work-methods="workMethods" :readonly="readonly" :saving="saving" @edit="editingRow = row" @copy="copyRow(row)" @remove="removeRow(row)" />
        </div>
      </draggable>
    </template>

    <div class="d-flex align-center flex-wrap mt-2">
      <v-menu v-if="!readonly" offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn small text color="primary" :disabled="saving" v-bind="attrs" v-on="on">
            <v-icon left class="no-gradient">mdi-plus</v-icon>
            {{ $t('quotations.calc.add_site_row') }}
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item v-for="type in rowTypes" :key="type" @click="addRow(type)">
            <v-list-item-content>
              <v-list-item-title>{{ $t(`quotations.calc.row_types.${type}`) }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t(`quotations.calc.row_type_hints.${type}`) }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-spacer />
      <span class="body-2 mr-3">{{ $t('quotations.calc.site_total') }}</span>
      <span class="body-2 font-weight-bold">{{ $formatNumber(total, '0,0.00') }} €</span>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import QuotationRowView from './QuotationRowView';
import QuotationRowEditor from './QuotationRowEditor';
import { rowsTotal } from '@/helpers/quotationCalc';

export default {
  name: 'QuotationRowList',
  components: { draggable, QuotationRowView, QuotationRowEditor },
  props: {
    // Työmaan rivit järjestyksessä; lisähinnat (conditional) näytetään omana ryhmänään
    rows: { type: Array, required: true },
    workMethods: { type: Array, default: () => [] },
    items: { type: Array, default: () => [] },
    readonly: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      rowTypes: ['WORK', 'OTHER', 'ITEM', 'CONDITIONAL'],
      // Muokattavana oleva rivi; muut näytetään luettavina
      editingRow: null,
    };
  },
  computed: {
    priced() {
      return this.rows.filter(r => !r.conditional);
    },
    conditional() {
      return this.rows.filter(r => r.conditional);
    },
    total() {
      return rowsTotal(this.priced);
    },
  },
  methods: {
    key(row) {
      return row.id || row._key;
    },
    // Järjestys tallentuu rivien järjestyksenä; lisähinnat pysyvät hinnoiteltujen rivien perässä
    reorder(priced, conditional) {
      this.rows.splice(0, this.rows.length, ...priced, ...conditional);
    },
    addRow(type) {
      const conditional = type === 'CONDITIONAL';
      const row = {
        _key: this.$randomString(),
        rowType: conditional ? 'OTHER' : type,
        workMethodId: null,
        attributeValues: [],
        item: null,
        description: '',
        quantity: type === 'WORK' ? 1 : null,
        unit: type === 'WORK' ? 'erä' : conditional ? '€/m' : '',
        unitPrice: null,
        conditional,
        conditionText: '',
      };
      if (conditional) this.rows.push(row);
      else this.reorder([...this.priced, row], this.conditional);
      this.editingRow = row;
    },
    // Kopio rivin perään, esim. toinen samanlainen poraus
    copyRow(row) {
      const copy = { ...JSON.parse(JSON.stringify(row)), id: null, _key: this.$randomString() };
      this.rows.splice(this.rows.indexOf(row) + 1, 0, copy);
      this.editingRow = copy;
    },
    hasData(row) {
      return !!(row.description || row.item || row.workMethodId || row.unitPrice);
    },
    async removeRow(row) {
      if (this.hasData(row) && !(await this.$confirmDelete(this.$t('quotations.calc.delete_row_confirm', { name: row.description || '' })))) return;
      if (this.editingRow === row) this.editingRow = null;
      const idx = this.rows.indexOf(row);
      if (idx > -1) this.rows.splice(idx, 1);
    },
  },
};
</script>

<style scoped>
.row-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
